<script setup>
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { SwButton } from '@getswish/design-library-web';
import { computed } from 'vue';

const props = defineProps({
  fields: {
    type: Object,
    default: () => {},
  },
});

const renderedText = computed(() => documentToHtmlString(props.fields.text));
const numOfImages = computed(() => (props.fields.images ? props.fields.images.length : 0));
const hasButton = computed(() => props.fields.buttonLink && props.fields.buttonText);
const buttonId = computed(() => {
  const id = props.fields.buttonText.toLowerCase().replace(/\s+/g, '');
  return `info-card-action-${id}`;
});
</script>

<template>
  <div class="info-card" :class="{ 'single-image': numOfImages === 1 }">
    <div class="content-wrapper">
      <h1>{{ fields.heading }}</h1>
      <div class="text" v-html="renderedText" />
    </div>

    <div class="image-single" v-if="numOfImages === 1">
      <img :src="fields.images[0].fields.file.url" :alt="fields.images[0].fields.title" />
    </div>

    <div class="images-wrapper" v-else-if="numOfImages > 1">
      <div class="image" v-for="image in fields.images" :key="image.id">
        <img :src="image.fields.file.url" :alt="image.fields.title" />
      </div>
    </div>

    <div class="footer">
      <SwButton
        v-if="hasButton"
        :id="buttonId"
        :label="fields.buttonText"
        element="a"
        :href="fields.buttonLink"
        variant="secondary"
        target="_blank"
        class="button"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.button {
  margin-top: $space-12;
}

.info-card {
  background: $primary-background;
  border-radius: $border-radius-x-small;
  width: 100%;
  box-shadow: $shadow-low-elevation;
  display: flex;
  flex-wrap: wrap;
  padding: $space-24 $space-16;
  max-width: 100%;
  margin: $space-24 0 0;

  @include desktop-small {
    max-width: 50%;
    padding: $space-32 $space-32 $space-24;
    margin: $space-24 $space-24 0 0;
  }

  &:last-of-type {
    margin-right: 0;
  }

  &:only-child {
    max-width: 100%;

    @include desktop-small {
      max-width: 573px;
    }
  }

  &.single-image {
    display: flex;
    align-items: center;

    .content-wrapper {
      width: 100%;

      @include tablet-large {
        width: 70%;
        padding-right: $space-24;
      }
    }

    .image-single {
      width: 100%;

      @include tablet-large {
        width: 30%;
      }

      img {
        max-width: 100%;
      }
    }
  }

  h1 {
    @include tertiary-title-medium;
    margin: $space-8 0 0;
  }

  .text :deep(p) {
    @include secondary-text-regular;
    margin-top: $space-12;
    margin-bottom: calc($space-16 / 2);
  }

  .images-wrapper,
  .footer {
    width: 100%;
  }

  .images-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: calc($space-16 / 2);
    flex-wrap: wrap;

    @include tablet-large {
      flex-wrap: nowrap;
    }

    .image {
      margin-right: $space-12;
      max-width: 120px;
      width: 100%;
      margin-bottom: $space-12;

      @include tablet-large {
        margin-bottom: 0;
      }

      &:last-of-type {
        margin-right: 0;
      }

      img {
        max-width: 100%;
      }
    }
  }

  .link {
    align-self: flex-end;
  }
}
</style>
